import {
  EXTERNAL_DISPLAY_ROLES,
  ExternalDisplayRole,
  MultiDisplayLayoutStateMap,
  WindowManagementPermissionState,
} from "./external_display_types";

export type WindowPlacementBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
  screenId: string | null;
};

export type ResolvedWindowPlacements = {
  permissionState: WindowManagementPermissionState;
  placements: Record<ExternalDisplayRole, WindowPlacementBounds>;
};

function normalizeNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function getScreenIdentifier(screen: Partial<ScreenDetailed>): string | null {
  if (typeof screen.label === "string" && screen.label.trim() !== "") {
    return screen.label;
  }

  const left = normalizeNumber(screen.availLeft, 0);
  const top = normalizeNumber(screen.availTop, 0);
  const width = normalizeNumber(screen.availWidth, 0);
  const height = normalizeNumber(screen.availHeight, 0);

  return `${left}:${top}:${width}:${height}`;
}

function screenToBounds(screen: Partial<ScreenDetailed>): WindowPlacementBounds {
  return {
    left: normalizeNumber(screen.availLeft, 0),
    top: normalizeNumber(screen.availTop, 0),
    width: normalizeNumber(screen.availWidth, normalizeNumber(screen.width, 0)),
    height: normalizeNumber(screen.availHeight, normalizeNumber(screen.height, 0)),
    screenId: getScreenIdentifier(screen),
  };
}

// 연결된 모니터 개수에 따라 자동으로 열어야 할 외부 디스플레이 role 목록을 반환한다.
// - 모니터 1개(또는 0): 추가 화면이 없으므로 빈 목록(메인 윈도우만 사용).
// - 모니터 2개: 보조 화면 1개 → "right" 한 개만 연다.
// - 모니터 3개 이상: 양 옆 화면에 "left"/"right" 모두 연다(기존 동작).
// DOM에 의존하지 않는 순수 함수이므로 단위 테스트로 검증 가능하다.
export function getAutoOpenRoles(screenCount: number): ExternalDisplayRole[] {
  if (!Number.isFinite(screenCount) || screenCount < 2) {
    return [];
  }

  if (screenCount === 2) {
    return ["right"];
  }

  return ["left", "right"];
}

export function getDisplayRoutePath(
  baseUrl: string,
  role: ExternalDisplayRole,
): string {
  const normalizedBaseUrl = baseUrl === "/" ? "" : baseUrl.replace(/\/$/, "");
  return `${normalizedBaseUrl}/display/${role}`;
}

export function getExternalDisplayWindowName(
  role: ExternalDisplayRole,
): string {
  return `aceworks-external-display-${role}`;
}

export function buildPopupFeatures(bounds: WindowPlacementBounds): string {
  return [
    "popup=yes",
    `left=${Math.round(bounds.left)}`,
    `top=${Math.round(bounds.top)}`,
    `width=${Math.max(100, Math.round(bounds.width))}`,
    `height=${Math.max(100, Math.round(bounds.height))}`,
  ].join(",");
}

export async function queryWindowManagementPermission(
  targetWindow: Window,
): Promise<WindowManagementPermissionState> {
  if (!targetWindow.navigator?.permissions?.query) {
    return "unsupported";
  }

  try {
    const permissionStatus = await targetWindow.navigator.permissions.query({
      name: "window-management" as PermissionName,
    } as PermissionDescriptor);

    return permissionStatus.state;
  } catch (_err) {
    return "error";
  }
}

export function getFallbackPlacement(
  targetWindow: Window,
  role: ExternalDisplayRole,
): WindowPlacementBounds {
  const currentScreen = targetWindow.screen;
  const width = normalizeNumber(
    (currentScreen as ScreenDetailed).availWidth,
    currentScreen.width,
  );
  const height = normalizeNumber(
    (currentScreen as ScreenDetailed).availHeight,
    currentScreen.height,
  );
  const top = normalizeNumber((currentScreen as ScreenDetailed).availTop, 0);
  const currentLeft = normalizeNumber(
    (currentScreen as ScreenDetailed).availLeft,
    0,
  );
  const leftOffset = role === "left" ? -width : width;

  return {
    left: currentLeft + leftOffset,
    top,
    width,
    height,
    screenId: getScreenIdentifier(currentScreen as ScreenDetailed),
  };
}

function createFallbackPlacements(
  targetWindow: Window,
): Record<ExternalDisplayRole, WindowPlacementBounds> {
  return {
    left: getFallbackPlacement(targetWindow, "left"),
    right: getFallbackPlacement(targetWindow, "right"),
  };
}

// 화면 목록(정렬 전)에서 role 별 실제 화면 배치를 계산한다. DOM/권한에 의존하지 않는
// 순수 함수이므로 단위 테스트와 동기 캐시(refreshMonitorAvailability 폴링) 양쪽에서 재사용한다.
//
// 좌/우 분리 규칙:
//  - 화면을 availLeft 오름차순으로 정렬한다.
//  - left  → 가장 왼쪽 화면(screens[0])
//  - right → 가장 오른쪽 화면(screens[length-1])
// 모니터 3개(좌/콕핏/우)인 경우에도 좌=screens[0], 우=screens[2] 가 되어
// 가운데(콕핏/opener) 화면은 좌/우 어느 쪽에도 선택되지 않는다.
// 화면이 없으면 null 을 반환해 호출 측이 fallback 으로 처리하게 한다.
export function computeRolePlacementsFromScreens(
  screens: ReadonlyArray<Partial<ScreenDetailed>>,
): Record<ExternalDisplayRole, WindowPlacementBounds> | null {
  const sorted = [...screens].sort(
    (a, b) => normalizeNumber(a.availLeft, 0) - normalizeNumber(b.availLeft, 0),
  );

  if (sorted.length === 0) {
    return null;
  }

  const leftScreen = sorted[0];
  const rightScreen = sorted[sorted.length - 1];

  return {
    left: screenToBounds(leftScreen),
    right: screenToBounds(rightScreen),
  };
}

export async function resolveWindowPlacements(
  targetWindow: Window,
): Promise<ResolvedWindowPlacements> {
  const fallbackPlacements = createFallbackPlacements(targetWindow);

  if (typeof targetWindow.getScreenDetails !== "function") {
    return {
      permissionState: "unsupported",
      placements: fallbackPlacements,
    };
  }

  const permissionState = await queryWindowManagementPermission(targetWindow);

  try {
    const screenDetails = await targetWindow.getScreenDetails();
    const placements = computeRolePlacementsFromScreens(
      screenDetails?.screens || [],
    );

    if (!placements) {
      return {
        permissionState,
        placements: fallbackPlacements,
      };
    }

    return {
      permissionState,
      placements,
    };
  } catch (_err) {
    return {
      permissionState: permissionState === "unsupported" ? "error" : permissionState,
      placements: fallbackPlacements,
    };
  }
}

export function openDisplayPopup(
  targetWindow: Window,
  url: string,
  role: ExternalDisplayRole,
  bounds: WindowPlacementBounds,
): Window | null {
  return targetWindow.open(
    url,
    getExternalDisplayWindowName(role),
    buildPopupFeatures(bounds),
  );
}

// GNOME(mutter) 같은 창 관리자는 새 창을 매핑할 때 자체 배치로 window.open/moveTo 좌표를
// 덮어쓴다(운영 PC 에서 left 창이 가운데, right 창이 왼쪽 모니터로 감). 창이 자리 잡은 뒤
// 목표 화면에서 벗어나 있으면 다시 옮긴다 — 매핑 이후의 moveTo 는 창 관리자가 따른다.
export const POPUP_PLACEMENT_RECHECK_MS = [300, 1000, 2500];

// 크기 → 위치 순서로 호출한다. Chrome 의 resizeTo 는 아직 반영되지 않은 moveTo 이전 좌표로
// 창 경계를 다시 잡아 직전 이동을 덮어쓴다(운영 PC 실측: move→resize 는 제자리, resize→move 는 이동).
function applyPopupBounds(
  popupWindow: Window,
  bounds: WindowPlacementBounds,
): void {
  try {
    popupWindow.resizeTo?.(Math.round(bounds.width), Math.round(bounds.height));
  } catch (_err) {
    // Some browser environments reject resizing.
  }

  try {
    popupWindow.moveTo?.(Math.round(bounds.left), Math.round(bounds.top));
  } catch (_err) {
    // Some browser environments reject repositioning.
  }
}

// 창 좌상단이 목표 화면 크기의 절반 이내면 같은 모니터에 있다고 본다(타이틀바/패널 오프셋 허용).
// 위치를 읽을 수 없으면(교차 출처/목 객체) 판단하지 않고 제자리로 간주한다.
function isPopupNearBounds(
  popupWindow: Window,
  bounds: WindowPlacementBounds,
): boolean {
  try {
    const { screenX, screenY } = popupWindow;
    if (typeof screenX !== "number" || typeof screenY !== "number") {
      return true;
    }
    return (
      Math.abs(screenX - bounds.left) < bounds.width / 2 &&
      Math.abs(screenY - bounds.top) < bounds.height / 2
    );
  } catch (_err) {
    return true;
  }
}

export function movePopupToBounds(
  popupWindow: Window,
  bounds: WindowPlacementBounds,
): void {
  applyPopupBounds(popupWindow, bounds);

  POPUP_PLACEMENT_RECHECK_MS.forEach((delay) => {
    setTimeout(() => {
      if (popupWindow.closed || isPopupNearBounds(popupWindow, bounds)) {
        return;
      }
      applyPopupBounds(popupWindow, bounds);
    }, delay);
  });
}

export function applyPermissionStateToLayout(
  previous: MultiDisplayLayoutStateMap,
  permissionState: WindowManagementPermissionState,
): MultiDisplayLayoutStateMap {
  return EXTERNAL_DISPLAY_ROLES.reduce((acc, role) => {
    acc[role] = {
      ...previous[role],
      permissionState,
    };
    return acc;
  }, { ...previous });
}
